import { afterNextRender, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { CounterWidget } from '../../../domain/counter';

@Component({
    selector: 'app-counter',
    imports: [],
    templateUrl: './counter.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './counter.component.scss'
})
export class CounterComponent implements OnDestroy {
  @Input() widget: CounterWidget = new CounterWidget()
  values: number[] = [];

  private started = false;
  private revealObserver?: IntersectionObserver;
  private frameIds: number[] = [];

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
  ) {
    afterNextRender(() => {
      this.resetValues();
      this.setupObserver();
      this.startIfVisible();
    });
  }

  displayValue(index: number): string {
    const target = Number(this.widget.counters[index]?.count ?? 0);
    const value = this.values[index] ?? 0;
    if (!Number.isInteger(target)) {
      return value.toFixed(1);
    }
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  }

  startCounters(): void {
    if (this.started || typeof window === 'undefined' || this.widget.counters.length === 0) {
      return;
    }
    this.started = true;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      this.values = this.widget.counters.map(center => Number(center.count));
      this.cdr.detectChanges();
      return;
    }

    this.values = this.widget.counters.map(() => 0);
    this.cdr.detectChanges();

    const duration = 1400;
    const start = performance.now();
    const targets = this.widget.counters.map(center => Number(center.count));

    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.values = targets.map((target) => {
        const value = target * eased;
        return Number.isInteger(target) ? value : Math.round(value * 10) / 10;
      });
      this.cdr.detectChanges();
      if (progress < 1) {
        this.frameIds.push(requestAnimationFrame(tick));
      } else {
        this.values = targets.slice();
        this.cdr.detectChanges();
      }
    };

    this.frameIds.push(requestAnimationFrame(tick));
  }

  private resetValues(): void {
    this.values = this.widget.counters.map(() => 0);
    this.cdr.detectChanges();
  }

  private startIfVisible(): void {
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    if (rect.bottom > 80 && rect.top < window.innerHeight - 40) {
      this.startCounters();
    }
  }

  private setupObserver(): void {
    this.revealObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.startCounters();
          this.revealObserver?.disconnect();
        }
      });
    }, { threshold: 0.35 });

    this.revealObserver.observe(this.elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.revealObserver?.disconnect();
    this.frameIds.forEach(id => cancelAnimationFrame(id));
  }
}
